import { MUIButton, MUIList } from "@abb/arcadia-ui-react"
import { ReactNode, useCallback, useState } from "react"
import { FieldValues, UseFormReturn } from "react-hook-form"
import { DiMaProgress } from "./DiMaProgress"

export interface DiMaDetailsContentItem {
  id: number
  label?: string
  displayItem?: ReactNode
  editItem?: ReactNode
}

export interface DiMaDetailsContentProps<T extends FieldValues = FieldValues> {
  readonly formMethods?: UseFormReturn<T>
  readonly canWrite?: boolean
  readonly listItems: DiMaDetailsContentItem[]
  readonly onSubmit?: (values: T) => Promise<void>
  readonly inlineEdit?: boolean
}

export function DiMaDetailsContent<T extends FieldValues>({
  formMethods,
  canWrite = true,
  listItems,
  onSubmit,
  inlineEdit = false,
}: DiMaDetailsContentProps<T>) {
  const [editing, setEditing] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const isSubmitting = formMethods?.formState.isSubmitting ?? false
  const isDirty = formMethods?.formState.isDirty ?? false
  const isValid = formMethods?.formState.isValid ?? false

  const resetForm = useCallback(() => formMethods?.reset(), [formMethods])

  const handleSave = useCallback(() => {
    if (formMethods && onSubmit) {
      formMethods.handleSubmit(async (values) => {
        await onSubmit(values)
        setEditing(false)
      })()
    }
  }, [formMethods, onSubmit])

  const handleCancel = useCallback(() => {
    resetForm()
    setEditing(false)
  }, [resetForm])

  const handleListItemClick = useCallback(
    (index: number) => {
      setSelectedRows([index])
      if (inlineEdit && canWrite && listItems[index]?.editItem) {
        setEditing(true)
      }
    },
    [inlineEdit, canWrite, listItems]
  )

  const handleClickEdit = useCallback(
    (index: number) => {
      setSelectedRows([index])
      if (inlineEdit && canWrite && listItems[index]?.editItem) {
        setEditing(true)
      }
    },
    [inlineEdit, canWrite, listItems]
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
      {canWrite && editing && (
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
          }}
        >
          <MUIButton
            variant="secondary"
            disabled={isSubmitting}
            onClickButton={handleCancel}
            label="Cancel"
            size={32}
          />

          <MUIButton
            variant="primary"
            isLoading={isSubmitting}
            disabled={isSubmitting || !isDirty || !isValid}
            onClickButton={handleSave}
            label="Save"
            size={32}
          />
        </div>
      )}

      {isSubmitting ? (
        <DiMaProgress />
      ) : (
        <MUIList
          listItems={listItems.map((item) => ({
            id: String(item.id),
            title: item.label ?? "",
            description:
              inlineEdit && editing && item.editItem
                ? ((item.editItem as string) ?? "")
                : ((item.displayItem as string) ?? ""),
          }))}
          listWithDivider
          size={32}
          selected={selectedRows}
          handleListItemClick={handleListItemClick}
          onClickEdit={handleClickEdit}
        />
      )}
    </div>
  )
}
